// src/components/game/tasks/ImmigrationQuiz.jsx
import React, { useState } from 'react';

// --- EXTENDED KNOWLEDGE BASE ---
const KNOWLEDGE_BASE = {
    Canada: {
        intro: "Canada uses the Express Entry system. It is competitive and points-based.",
        topics: [
            { q: "How much money do I need?", a: "Single applicant: ~$13,757 CAD liquid funds (6-month history). No credit cards." },
            { q: "Do I need a job offer?", a: "Not for Federal Skilled Worker, but it helps. Without one, you need CLB 9+ English." },
            { q: "Timeline?", a: "Ideally 6 months, often 12-18. Security screening is the wildcard." }
        ]
    },
    // --- UPDATED UK DATA ---
    UK: {
        intro: "The UK system is employer-led and highly regulated. You need a licensed sponsor before you can even apply.",
        topics: [
            // 1. JOB STANDARDS
            { q: "Does my job meet the threshold?", a: "We check your 'Occupation Code' against the UKVI list. Salary must meet the 'going rate' (usually £38,700+) unless it's on the Immigration Salary List." },
            { q: "How do I prove qualifications?", a: "You likely need a 'Statement of Comparability' from UK ENIC to prove your degree matches UK standards." },
            
            // 2. LICENSING
            { q: "Do I need a license first?", a: "For regulated jobs (Doctors, Nurses, Engineers), YES. You must register with the GMC/NMC *before* visa approval." },
            { q: "Will my license transfer?", a: "Rarely. You often need to pass UK exams (PLAB, CBT) or complete an adaptation period." },
            
            // 3. EMPLOYER CHECKS
            { q: "Is my employer valid?", a: "They MUST hold an A-rated 'Sponsor Licence'. If they lose it, your visa is cancelled immediately." },
            { q: "What is a CoS?", a: "Certificate of Sponsorship. It's a digital reference number. No CoS = No Visa Application." },
            
            // 4. COSTS
            { q: "Total Costs?", a: "High. Visa Fee + Legal Fee + Immigration Health Surcharge (IHS) of ~£1,035/year per person." }
        ]
    },
    // -----------------------
    USA: {
        intro: "US immigration is employer-driven. H-1B is a lottery, not a guarantee.",
        topics: [
            { q: "Can I self-sponsor?", a: "Only via EB-1 (Extraordinary Ability) or EB-5 (Investment >$800k). Otherwise, no employer = no visa." },
            { q: "H-1B Lottery?", a: "Random selection. Even with a job offer, chance of selection is ~25%." }
        ]
    },
    Australia: {
        intro: "Australia uses SkillSelect. Points based on Age, English, and Occupation Lists.",
        topics: [
            { q: "Age limit?", a: "You lose points rapidly after 30. At 45, you are generally ineligible." },
            { q: "189 vs 190 Visa?", a: "189 is Federal (freedom). 190 is State (must live there 2 years)." }
        ]
    },
    NewZealand: {
        intro: "Values health and the 'Green List'. Health checks are strict.",
        topics: [
            { q: "Green List?", a: "Fast-track residency for Tier 1 roles (Engineering, Health, ICT)." },
            { q: "Health Standards?", a: "High BMI or chronic conditions often lead to rejection due to 'cost to health system'." }
        ]
    }
};

export default function ImmigrationQuiz({ country, npcData, onComplete }) {
  const [phase, setPhase] = useState('INTRO');
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  const kb = KNOWLEDGE_BASE[country] || KNOWLEDGE_BASE['Canada'];
  // Fallback quiz data if database hasn't loaded
  const quizData = npcData?.dialogueTree?.[country] || { quizQuestion: "Proceed with application?", options: [{text:"Yes", correct:true}] };

  if (phase === 'INTRO') {
      return (
          <div className="bg-gray-900/95 p-8 rounded-xl border-l-4 border-blue-500 max-w-3xl text-left shadow-2xl animate-fade-in">
              <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-blue-400">IMMIGRATION CONSULTANT</h3>
                  <span className="text-xs text-gray-500 uppercase tracking-widest">{country} DESK</span>
              </div>
              <p className="text-white text-lg mb-8 leading-relaxed font-serif">
                  "{kb.intro}" <br/>
                  <span className="text-sm text-gray-400 italic">"I charge by the minute. What do you need to verify?"</span>
              </p>
              <button onClick={() => setPhase('CONSULT')} className="w-full bg-blue-700 hover:bg-blue-600 text-white px-6 py-4 rounded font-bold text-lg transition-all">START CONSULTATION</button>
          </div>
      );
  }

  if (phase === 'CONSULT') {
      return (
          <div className="bg-gray-900/95 p-8 rounded-xl border-l-4 border-cyan-500 max-w-3xl text-left shadow-2xl h-[550px] flex flex-col">
              <h3 className="text-xl font-bold text-cyan-400 mb-4">CONSULTATION LOG:</h3>
              <div className="flex-grow space-y-3 overflow-y-auto pr-2 custom-scrollbar">
                  {selectedTopic ? (
                      <div className="bg-black/50 p-6 rounded border border-gray-600 animate-fade-in">
                          <p className="text-yellow-400 text-sm mb-2 uppercase tracking-wide">Q: {selectedTopic.q}</p>
                          <p className="text-white text-lg leading-relaxed">"{selectedTopic.a}"</p>
                          <button onClick={() => setSelectedTopic(null)} className="mt-6 text-cyan-400 hover:text-white text-sm underline flex items-center gap-2"><span>&lt;</span> Ask another question</button>
                      </div>
                  ) : (
                      kb.topics.map((t, i) => (
                          <button key={i} onClick={() => setSelectedTopic(t)} className="w-full text-left p-4 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500 rounded transition-all flex justify-between items-center group">
                              <span className="text-gray-200 group-hover:text-white font-medium text-sm">{t.q}</span>
                              <span className="text-cyan-600 group-hover:text-cyan-400 text-lg">?</span>
                          </button>
                      ))
                  )}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-800">
                  <p className="text-xs text-gray-500 mb-2">Once you are confident in your eligibility:</p>
                  <button onClick={() => setPhase('QUIZ')} className="w-full bg-yellow-700 hover:bg-yellow-600 text-white px-6 py-3 rounded font-bold transition-all">PROCEED TO ASSESSMENT</button>
              </div>
          </div>
      );
  }

  return (
      <div className="bg-gray-900/95 p-8 rounded-xl border-l-4 border-yellow-500 max-w-3xl text-left shadow-2xl animate-fade-in">
          <h3 className="text-xl font-bold text-yellow-400 mb-2">ELIGIBILITY CHECK</h3>
          <p className="text-white text-xl mb-8 font-serif">"{quizData.quizQuestion}"</p>
          <div className="grid gap-4">
              {quizData.options.map((opt, i) => (
                  <button key={i} onClick={() => onComplete(opt.correct)} className="text-left p-5 bg-black border border-gray-600 hover:border-yellow-500 hover:bg-gray-800 text-white rounded-lg transition-all text-lg">{opt.text}</button>
              ))}
          </div>
      </div>
  );
}